<!-- 
Dashboard for a levy collection.
-->
<template>
  <div>
    <our-heading 
      :breadcrumbs="getBreadcrumbs('dashboard')"
      :subtitle="statusDescription"
      title="Dashboard"
      subtitle-tooltip="Levy collection status"
      user-guide-topic="levy-collection-dashboard"
    >
      <template v-slot:navigator>
        <levy-collection-navigator :levy-collection-id="levyCollectionId" />
      </template>
    </our-heading>
    <div v-if="hasProviders">
      <v-layout align-center row wrap>
        <v-flex :shrink="!xs" :xs12="xs">
          <warnings-menu :levy-collection-id="levyCollectionId" css="ml-0"/>
        </v-flex>
        <v-flex :shrink="!xs" :xs12="xs">
          <exports-menu :levy-collection-id="levyCollectionId" :disabled="widgetsLoading || loading" />
        </v-flex>
        <v-flex :shrink="!xs" :xs12="xs">
          <v-btn :disabled="widgetsLoading || loading" :block="xs" class="mb-3" @click="showDashboardSettings = true">
            <v-icon class="mr-2">settings</v-icon>
            Dashboard settings
          </v-btn>
        </v-flex>
        <v-spacer/>
        <v-flex 
          v-if="totalLevy" 
          :class="{'mb-3': xs}" 
          :grow="!xs" :xs12="xs"
          class="text-sm-right text-xs-center"
        >
          <span class="grey--text text--darken-1">Total levy amount: </span>
          <span class="font-weight-medium mr-2">{{ totalLevy }}</span>
        </v-flex>
      </v-layout>
      <our-loading-indicator v-if="widgetsLoading || loading"/>
      <v-alert v-if="noWidgets && !loading && !widgetsLoading" :value="true" type="info" class="mt-4">
        You have not chosen any dashboard widgets to display.
        Click the <i>Dashboard settings</i> button to select the widgets you want to include.
      </v-alert>
      <v-scale-transition>
        <v-container v-if="!noWidgets && !loading && !widgetsLoading" fluid grid-list-sm pa-0>
          <draggable v-model="widgetsForLevyCollection" v-bind="getOptions()" class="layout wrap">
            <template v-for="widget of widgetsForLevyCollection">
              <component
                :is="widget.widget" 
                :key="widget.widget"
                :levy-collection-id="levyCollectionId" 
                :toggled="widget.toggled"
                class="widget"
                @toggle="toggleWidget(widget)"
                @removeWidget="removeWidget(widget)" 
                @showTasks="showWorkflow" 
                @runTask="runTask($event)" 
                @showProviders="showProviders"
                @showInvoices="showInvoices"
              />
            </template>
          </draggable>
        </v-container>
      </v-scale-transition>
    </div>
    <our-getting-started-hint v-else :levy-collection-id="levyCollectionId">
      <p>
        Welcome to the {{ levyCollectionDescription }} levy collection.
      </p>
      <p>
        The first steps in preparing a levy collection involve reviewing and editing the various parameters and templates
        that were copied over from the prior year's levy collection. 
      </p>
      <p>
        If you are not sure whether something needs to be changed, you can always skip over it and come back to it later. 
        You can return to this screen later by clicking on <i>Setup</i> in the navigation sidebar.
      </p>
    </our-getting-started-hint>
    <dashboard-settings-dialog 
      v-if="levyCollection.type"
      v-model="showDashboardSettings" 
      :levy-collection-id="levyCollectionId"
      :levy-collection-type="levyCollection.type"
    />
    <run-all-provider-tasks-dialog 
      v-model="showRunTaskDialog" 
      :levy-collection-id="levyCollectionId" 
      :task="taskToRun"
    />
  </div>
</template>

<script>
/* 
Levy collection dashboard
-------------------------
Displays dashboard widgets, total levy, warnings menu and dashboard settings.
*/
import DashboardSettingsDialog from "@/components/levy-collections/DashboardSettingsDialog.vue";
import draggable from "vuedraggable";
import ActivityWidget from "@/components/levy-collections/widgets/ActivityWidget.vue";
import InvoiceStatusWidget from "@/components/levy-collections/widgets/InvoiceStatusWidget.vue";
import PaymentStatusWidget from "@/components/levy-collections/widgets/PaymentStatusWidget.vue";
import LevyCollectionNavigator from "@/components/levy-collections/LevyCollectionNavigator.vue";
import LevyComponentsWidget from "@/components/levy-collections/widgets/LevyComponentsWidget.vue";
import NoticeCountsWidget from "@/components/levy-collections/widgets/NoticeCountsWidget.vue";
import ProviderLevyAmountsWidget from "@/components/levy-collections/widgets/ProviderLevyAmountsWidget.vue";
import ProviderStatusWidget from "@/components/levy-collections/widgets/ProviderStatusWidget.vue";
import RunAllProviderTasksDialog from "@/components/levy-collections/RunAllProviderTasksDialog.vue";
import StatusTimeline from "@/components/levy-collections/StatusTimeline.vue";
import WarningsAndFlagsWidget from "@/components/levy-collections/widgets/WarningsAndFlagsWidget.vue";
import WarningsMenu from "@/components/levy-collections/WarningsMenu.vue";
import ExportsMenu from "@/components/levy-collections/ExportsMenu.vue";
import levyCollectionMixins from "./mixins";
import * as action from "@/store/users/action-types";
import * as mutation from "@/store/users/mutation-types";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import * as formatting from "@/helpers/formatting.helper";
import { deepCopy } from "@/store/helpers";

export default {
  components: {
    DashboardSettingsDialog,
    //eslint-disable-next-line vue/component-options-name-casing
    draggable,
    ActivityWidget,
    InvoiceStatusWidget,
    PaymentStatusWidget,
    LevyCollectionNavigator,
    LevyComponentsWidget,
    NoticeCountsWidget,
    ProviderLevyAmountsWidget,
    ProviderStatusWidget,
    RunAllProviderTasksDialog,
    StatusTimeline,
    WarningsAndFlagsWidget,
    WarningsMenu,
    ExportsMenu
  },
  mixins: [levyCollectionMixins],
  data() {
    return {
      showDashboardSettings: false,
      showRunTaskDialog: false,
      redirectIfSandbox: true,
      taskToRun: {
        type: null,
        description: null
      }
    };
  },
  computed: {
    hasProviders() {
      return !this.$store.state.levyCollectionProviders.listing.empty;
    },
    totalLevy() {
      return this.$store.state.levyCollections.view.loaded
        ? formatting.currency(this.$store.state.levyCollections.view.data.totalLevy)
        : null;
    },
    state() {
      return this.$store.state.users.widgets;
    },
    widgetsLoading() {
      return this.state.loading;
    },
    widgetsLoaded() {
      return this.state.loaded;
    },
    widgets() {
      return this.state.data;
    },
    widgetsForLevyCollection: {
      get() {
        return this.state.data[this.levyCollection.type] || [];
      },
      set(value) {
        const copy = deepCopy(this.widgets);
        copy[this.levyCollection.type] = value;
        this.$store.dispatch(action.WIDGETS_SAVE, copy);
      }
    },
    noWidgets() {
      return this.loaded && this.widgetsForLevyCollection.length == 0;
    },
    xs() {
      return this.$vuetify.breakpoint.xsOnly;
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    widgetsLoaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    async refresh() {
      const promises = [this.$store.dispatch(lcpAction.LIST_REFRESH, this.levyCollectionId)];
      const needsRefreshed = !this.state.loaded && !this.state.loading;
      if (needsRefreshed) {
        promises.push(this.$store.dispatch(action.WIDGETS_GET));
      }
      await Promise.all(promises);
    },
    removeWidget(widget) {
      this.$store.commit(mutation.WIDGET_REMOVE, {
        levyCollectionType: this.levyCollection.type,
        widget: widget.widget
      });
      this.$store.dispatch(action.WIDGETS_SAVE);
    },
    toggleWidget(widget) {
      this.$store.commit(mutation.WIDGET_TOGGLE, {
        levyCollectionType: this.levyCollection.type,
        widget: widget.widget
      });
      this.$store.dispatch(action.WIDGETS_SAVE);
    },
    getOptions() {
      return {
        handle: ".drag-handle",
        animation: 150
      };
    },
    showProviders() {
      this.$router.push(`/levy-collections/${this.levyCollectionId}/providers`);
    },
    showInvoices() {
      this.$router.push(`/levy-collections/${this.levyCollectionId}/invoices`);
    },
    showWorkflow() {
      this.$router.push(`/levy-collections/${this.levyCollectionId}/workflow`);
    },
    runTask(task) {
      this.taskToRun = task;
      this.showRunTaskDialog = true;
    }
  }
};
</script>
